/**
 * ePSF access — licensed pharmacy product (separate from eISF).
 */
import { useMemo } from "react";
import { Navigate } from "react-router-dom";
import { useUserData } from "../../../hooks/useUserData";
import {
  parsePermissions,
  hasPermission,
  getAllPermissionKeys,
  isSystemAdministratorRole,
  getSessionRoleName,
  isSessionSuperAdmin,
} from "../../../utils/permissionUtils";
import { organizationHasEpsfModule } from "../../../utils/orgModuleAccess";
import {
  hasGrantedModule,
  isSponsorCroSession,
} from "../../../utils/sponsorCroAccess";
import { SIDEBAR_ORG_MODULES } from "../../../utils/sidebarOrgModuleFilter";

export function useEpsfAccess() {
  const { userData, loading } = useUserData();
  const sessionRole = getSessionRoleName(userData);
  const isSystemAdmin =
    isSystemAdministratorRole(sessionRole) || isSessionSuperAdmin(userData);
  const isExternal = isSponsorCroSession(userData);

  const permissions = useMemo(
    () =>
      isSystemAdmin && !isExternal
        ? getAllPermissionKeys()
        : parsePermissions(userData?.Permission || ""),
    [isSystemAdmin, isExternal, userData?.Permission],
  );

  const moduleAccess =
    (isSystemAdmin && !isExternal) ||
    organizationHasEpsfModule(userData) ||
    (isExternal && hasGrantedModule(userData, SIDEBAR_ORG_MODULES.EPSF));

  const can = (key) => {
    if (isSystemAdmin && !isExternal) return true;
    if (!moduleAccess) return false;
    return hasPermission(permissions, key);
  };

  const canView =
    can("VIEW_EPSF") ||
    can("EPSF_DASHBOARD") ||
    can("EPSF_REPORTS") ||
    can("EPSF_RECEIVE") ||
    can("EPSF_DISPENSE") ||
    can("EPSF_INVENTORY_VIEW") ||
    can("EPSF_IMP_PRODUCT_MANAGE") ||
    can("EPSF_TREATMENT_SETUP") ||
    can("EPSF_KIT_TREATMENT_MANAGE") ||
    can("EPSF_EVIDENCE");

  return {
    userData,
    loading,
    isSystemAdmin: isSystemAdmin && !isExternal,
    isExternal,
    moduleAccess,
    canView,
    canReceive: can("EPSF_RECEIVE"),
    canRelease: can("EPSF_RELEASE"),
    canDispense: can("EPSF_DISPENSE"),
    canReturn: can("EPSF_RETURN"),
    canDestroy: can("EPSF_DESTROY"),
    canReconcile: can("EPSF_RECONCILE"),
    canUnblinded: can("EPSF_UNBLINDED"),
    canEmergencyUnblind: can("EPSF_EMERGENCY_UNBLIND"),
    canProductManage: can("EPSF_IMP_PRODUCT_MANAGE"),
    canTreatmentSetup: can("EPSF_TREATMENT_SETUP"),
    canKitTreatmentManage: can("EPSF_KIT_TREATMENT_MANAGE"),
    canEvidence: can("EPSF_EVIDENCE"),
    canReports: can("EPSF_REPORTS"),
    canInventoryView:
      can("EPSF_INVENTORY_VIEW") ||
      can("EPSF_RECEIVE") ||
      can("EPSF_DISPENSE") ||
      can("EPSF_RETURN") ||
      can("EPSF_RECONCILE") ||
      can("EPSF_DESTROY") ||
      can("EPSF_RELEASE"),
    can,
  };
}

/** Whether a workspace/sidebar nav item is visible for the current effective permissions. */
export function epsfNavItemAllowed(item, access) {
  if (!item || !access?.moduleAccess) return false;
  if (item.evidence && !access.canEvidence) return false;
  const keys = Array.isArray(item.permissionKeys)
    ? item.permissionKeys
    : item.permissionKey
      ? [item.permissionKey]
      : [];
  if (!keys.length) return Boolean(access.canView);
  if (access.isSystemAdmin) return true;
  return keys.some((key) => access.can(key));
}

export function EpsfAccessGate({
  allow,
  loading,
  children,
  redirectTo = "/dashboard",
}) {
  if (loading) {
    return <div className="epsf-page epsf-page--bootstrapping" />;
  }
  if (!allow) {
    return <Navigate to={redirectTo} replace />;
  }
  return children;
}
